import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router';
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  InfoIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import * as z from 'zod';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getErrorCode, isHttpError } from '@/app/queryError';
import { Button } from '@/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from '@/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/ui/input-group';
import { Spinner } from '@/ui/spinner';
import { loginSchema, type LoginValues } from '@/schemas/login';
import { AuthCard } from '@/layouts/AuthLayout';

const EMPTY_VALUES: LoginValues = { email: '', password: '' };

type LoginFormProps = {
  onSubmit: (values: LoginValues) => void;
  onEdit?: () => void;
  submitting?: boolean;
  error?: FetchBaseQueryError | SerializedError;
};

export function LoginForm({ onSubmit, onEdit, submitting = false, error }: LoginFormProps) {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);

  const result = loginSchema.safeParse(values);
  const fieldErrors = result.success ? {} : z.flattenError(result.error).fieldErrors;
  const emailError = touched.email ? fieldErrors.email?.[0] : undefined;
  const passwordError = touched.password ? fieldErrors.password?.[0] : undefined;
  const emailValid = !fieldErrors.email && !emailError;

  const unauthorized = isHttpError(error) && error.status === 401;
  const mfaRequired = unauthorized && getErrorCode(error) === 'mfa_required';
  const passwordMessage = unauthorized && !mfaRequired ? 'Incorrect password. Try again.' : passwordError;

  let formMessage: string | undefined;

  if (mfaRequired)
    formMessage = 'Your account requires a second factor, which the dashboard does not support yet. Contact your administrator.';
  else if (error && !unauthorized)
    formMessage = "We couldn't sign you in right now. Check your connection and try again.";

  const change = (field: keyof LoginValues) => (e: ChangeEvent<HTMLInputElement>) => {
    setValues((current) => ({ ...current, [field]: e.target.value }));
    onEdit?.();
  };
  const blur = (field: keyof LoginValues) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ email: true, password: true });
    if (result.success && !submitting) onSubmit(result.data);
  };

  return (
    <AuthCard>
      <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-5">
        <FieldGroup className="gap-4">
          <Field className="gap-1.5">
            <FieldLabel htmlFor="login-email">Email</FieldLabel>
            <InputGroup>
              <InputGroupInput
                id="login-email"
                size="lg"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                required
                readOnly={submitting}
                value={values.email}
                onChange={change('email')}
                onBlur={blur('email')}
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'login-email-error' : undefined}
              />

              {emailValid && (
                <InputGroupAddon align="inline-end">
                  <CheckCircleIcon weight="fill" className="size-4.5 text-success" aria-hidden />
                </InputGroupAddon>
              )}
            </InputGroup>

            {emailError && (
              <FieldError id="login-email-error" className="flex items-center gap-1">
                <WarningCircleIcon weight="fill" className="size-4 shrink-0" aria-hidden />
                {emailError}
              </FieldError>
            )}
          </Field>

          <Field className="gap-1.5">
            <FieldLabel htmlFor="login-password">Password</FieldLabel>
            <InputGroup>
              <InputGroupInput
                id="login-password"
                size="lg"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                readOnly={submitting}
                value={values.password}
                onChange={change('password')}
                onBlur={blur('password')}
                aria-invalid={Boolean(passwordMessage)}
                aria-describedby="login-password-hint"
              />

              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  size="icon-sm"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((shown) => !shown)}
                >
                  {showPassword ? (
                    <EyeIcon weight="regular" className="size-5" />
                  ) : (
                    <EyeSlashIcon weight="regular" className="size-5" />
                  )}
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>

            <div className="flex items-center justify-between gap-1.5">
              {passwordMessage ? (
                <FieldError id="login-password-hint" className="flex items-center gap-1">
                  <WarningCircleIcon weight="fill" className="size-4 shrink-0" aria-hidden />
                  {passwordMessage}
                </FieldError>
              ) : (
                <FieldDescription
                  id="login-password-hint"
                  className="flex items-center gap-1 nth-last-2:mt-0"
                >
                  <InfoIcon weight="fill" className="size-4 shrink-0" aria-hidden />
                  Password is case sensitive
                </FieldDescription>
              )}

              <Button asChild variant="text" size="pill-sm">
                <Link to="/forgot-password">Forgot your password?</Link>
              </Button>
            </div>
          </Field>
        </FieldGroup>

        <div className="flex flex-col gap-2">
          {formMessage && (
            <FieldError className="flex items-start gap-1">
              <WarningCircleIcon weight="fill" className="mt-0.5 size-4 shrink-0" aria-hidden />
              {formMessage}
            </FieldError>
          )}

          <Button
            type="submit"
            variant="primary"
            size="pill-lg"
            className="w-full"
            disabled={!result.success || Boolean(error) || submitting}
          >
            {submitting && <Spinner data-icon="inline-start" />}
            Sign in
          </Button>

          <FieldSeparator className="my-0">Or</FieldSeparator>

          <Button type="button" variant="contrast" size="pill-lg" className="w-full" disabled={submitting}>
            <img src="/icons/google.svg" alt="" width={20} height={20} />
            Sign in with Google
          </Button>

          <p className="text-center text-body-xs text-muted-foreground">
            By signing in, you agree to our{' '}
            <Button type="button" variant="text" size="pill-sm" className="underline">
              Terms
            </Button>{' '}
            and acknowledge our{' '}
            <Button type="button" variant="text" size="pill-sm" className="underline">
              Privacy Policy
            </Button>
            .
          </p>
        </div>
      </form>
    </AuthCard>
  );
}
